/**
 * MetricExplainer.jsx — Unified explainer for any metric in the app.
 *
 * Consumers pass {category, metricId, isOpen, onClose}. This component
 * reads the entry from `metric_dictionary.js` and renders a plain-Arabic
 * modal explaining WHAT the metric means and HOW it is computed.
 *
 * Design goals (owner directive 2026-07-23):
 *   • ONE modal for the whole app — every "?" hover/click uses this.
 *   • Plain Arabic — never leak thresholds without context, never leak code.
 *   • Extensible: works for segments today, works for KPI/ratio/benchmark
 *     tomorrow without any change to the component itself.
 *
 * ISOLATION: purely presentational. No network, no state beyond open/close
 * handling. Consumers own the open state.
 */
import React, { useEffect } from "react";
import { getMetric } from "./metric_dictionary";
import { zVar } from "../lib/zIndex";

export default function MetricExplainer({
  category = "segments",
  metricId,
  isOpen,
  onClose,
}) {
  const entry = metricId ? getMetric(category, metricId) : null;

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return undefined;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      dir="rtl"
      data-testid="metric-explainer-backdrop"
      className="me-wrap"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: zVar('extra_9999'),
        background: "rgba(0,0,0,0.62)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid={`metric-explainer-${category}-${metricId || "empty"}`}
        className="me-box"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          border: `1px solid ${entry?.color || "#D5C19E"}55`,
          borderRadius: 20, padding: "28px 26px",
          color: "#3D2A1C", maxWidth: 520, width: "100%",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
          boxShadow: "0 30px 80px rgba(61,42,28,0.35)",
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            {entry ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700,
                              letterSpacing: -0.5, color: entry.color }}>
                  {entry.title}
                </div>
                <div style={{ fontSize: 13.5, color: "#57534E",
                              marginTop: 6, lineHeight: 1.5 }}>
                  {entry.one_line}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                لا يوجد شرح لهذا المؤشر بعد
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="إغلاق"
            data-testid="metric-explainer-close"
            onClick={onClose}
            style={{
              background: "rgba(61,42,28,0.10)", border: "none",
              width: 32, height: 32, borderRadius: 8, cursor: "pointer",
              color: "#3D2A1C", fontSize: 18, lineHeight: 1,
            }}
          >×</button>
        </header>

        {!entry && (
          <div style={{ padding: 20, color: "#78716C", fontSize: 13,
                        textAlign: "center" }}>
            سنضيف تعريف هذا المؤشر ({category} · {String(metricId) || "—"})
            إلى القاموس المركزي قريبًا.
          </div>
        )}

        {entry && (
          <div style={{ display: "grid", gap: 16 }}>
            <Section
              data-testid={`metric-explainer-${metricId}-rule`}
              icon={"\u{1F4CB}"}
              label="كيف يُحسب"
              body={entry.rule_arabic}
            />
            {entry.how_to_use && (
              <Section
                data-testid={`metric-explainer-${metricId}-usage`}
                icon={"\u{1F4A1}"}
                label="كيف تستفيد منه"
                body={entry.how_to_use}
              />
            )}
            <Section
              data-testid={`metric-explainer-${metricId}-source`}
              icon={"\u{1F517}"}
              label="مصدر البيانات"
              body={entry.source}
              muted
            />
            {entry.priority_note && (
              <div
                data-testid={`metric-explainer-${metricId}-priority`}
                style={{
                  fontSize: 11, color: "#78716C", textAlign: "center",
                  padding: "8px 12px",
                  background: "rgba(61,42,28,0.06)",
                  borderRadius: 10,
                }}
              >
                ℹ&nbsp; {entry.priority_note}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon, label, body, muted }) {
  return (
    <div style={{
      background: "rgba(61,42,28,0.06)", borderRadius: 14,
      padding: "14px 16px",
      border: "1px solid rgba(61,42,28,0.12)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                    fontSize: 12, color: "#78716C", marginBottom: 8,
                    fontWeight: 600 }}>
        <span aria-hidden>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={{
        fontSize: 14, lineHeight: 1.75,
        color: muted ? "#78716C" : "#3D2A1C",
      }}>
        {body}
      </div>
    </div>
  );
}
